import type { Db } from '../clients.ts';
import { scoped, type TenantScope } from '../tenant.ts';

/**
 * Store details, ported from ctrl-saas's `store-settings.loader.ts` and
 * `store-settings-actions.ts`.
 *
 * Runs against the *browser* client, so RLS is the enforcer: every member of
 * the account may read (20260828040000), and only `settings.manage` may write.
 * There is nothing here a server has to hold -- no secret, no credential
 * column, no read RLS cannot express -- so it stays off the admin API by the
 * rule in `docs/authorization.md`.
 *
 * `store_settings` is deliberately separate from `public.accounts`:
 * `accounts_self_update` restricts that table to the primary owner, so a
 * `settings.manage` member could never maintain store details there. The
 * trading name is the one field that does live on `accounts`, which is why it
 * is read and written separately below.
 */

export const WEIGHT_UNITS = ['g', 'oz', 'lb', 'kg'] as const;
export type WeightUnit = (typeof WEIGHT_UNITS)[number];

export interface StoreSettingsValues {
  legalName: string;
  supportEmail: string;
  supportPhone: string;
  supportUrl: string;
  websiteUrl: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  timezone: string;
  currency: string;
  weightUnit: WeightUnit;
  orderNumberPrefix: string;
  receiptFooter: string;
}

export interface StoreSettingsData {
  values: StoreSettingsValues;
  /** `accounts.name` -- the trading name, editable by the primary owner only. */
  tradingName: string;
  isPrimaryOwner: boolean;
  /** True when no `store_settings` row exists yet; the form saves by insert. */
  isNew: boolean;
}

/**
 * Column defaults mirrored from `43-store-settings.sql`.
 *
 * Accounts created before the table existed have no row at all, so the form
 * has to be able to render -- and save -- from nothing.
 */
export const EMPTY_STORE_SETTINGS: StoreSettingsValues = {
  legalName: '',
  supportEmail: '',
  supportPhone: '',
  supportUrl: '',
  websiteUrl: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: '',
  zip: '',
  country: 'US',
  timezone: 'America/Los_Angeles',
  currency: 'USD',
  weightUnit: 'g',
  orderNumberPrefix: '',
  receiptFooter: '',
};

const COLUMNS =
  'legal_name, support_email, support_phone, support_url, website_url, address_line1, address_line2, city, state, zip, country, timezone, currency, weight_unit, order_number_prefix, receipt_footer';

/** `weight_unit` is a text column with a CHECK, so it reads back as string. */
function asWeightUnit(value: string | null): WeightUnit {
  return WEIGHT_UNITS.find((unit) => unit === value) ?? EMPTY_STORE_SETTINGS.weightUnit;
}

export async function getStoreSettings(
  db: Db,
  scope: TenantScope,
  userId: string,
): Promise<StoreSettingsData> {
  const [settings, account] = await Promise.all([
    scoped(db.from('store_settings').select(COLUMNS), scope).maybeSingle(),
    db.from('accounts').select('name, primary_owner_user_id').eq('id', scope.accountId).single(),
  ]);

  if (settings.error) throw settings.error;
  if (account.error) throw account.error;

  const row = settings.data;

  return {
    isNew: !row,
    tradingName: account.data.name,
    isPrimaryOwner: account.data.primary_owner_user_id === userId,
    values: row
      ? {
          legalName: row.legal_name ?? '',
          supportEmail: row.support_email ?? '',
          supportPhone: row.support_phone ?? '',
          supportUrl: row.support_url ?? '',
          websiteUrl: row.website_url ?? '',
          addressLine1: row.address_line1 ?? '',
          addressLine2: row.address_line2 ?? '',
          city: row.city ?? '',
          state: row.state ?? '',
          zip: row.zip ?? '',
          country: row.country ?? EMPTY_STORE_SETTINGS.country,
          timezone: row.timezone ?? EMPTY_STORE_SETTINGS.timezone,
          currency: row.currency ?? EMPTY_STORE_SETTINGS.currency,
          weightUnit: asWeightUnit(row.weight_unit),
          orderNumberPrefix: row.order_number_prefix ?? '',
          receiptFooter: row.receipt_footer ?? '',
        }
      : EMPTY_STORE_SETTINGS,
  };
}

export class StoreSettingsError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'StoreSettingsError';
  }
}

/**
 * Save the store details.
 *
 * Update-then-insert rather than upsert, carried over from ctrl-saas.
 * PostgREST turns an upsert into `ON CONFLICT DO UPDATE SET ...` over every
 * column in the payload, `account_id` included -- and `account_id` is excluded
 * from the column-scoped UPDATE grant, which is exactly what stops a member
 * re-parenting a row into another tenant. The whole statement is rejected.
 */
export async function saveStoreSettings(
  db: Db,
  scope: TenantScope,
  values: StoreSettingsValues,
): Promise<void> {
  const payload = {
    legal_name: nullable(values.legalName),
    support_email: nullable(values.supportEmail),
    support_phone: nullable(values.supportPhone),
    support_url: normalizeUrl(values.supportUrl),
    website_url: normalizeUrl(values.websiteUrl),
    address_line1: nullable(values.addressLine1),
    address_line2: nullable(values.addressLine2),
    city: nullable(values.city),
    state: nullable(values.state),
    zip: nullable(values.zip),
    country: values.country.toUpperCase(),
    timezone: values.timezone,
    currency: values.currency.toUpperCase(),
    weight_unit: values.weightUnit,
    order_number_prefix: nullable(values.orderNumberPrefix),
    receipt_footer: nullable(values.receiptFooter),
  };

  const { data: updated, error } = await scoped(db.from('store_settings').update(payload), scope)
    .select('account_id')
    .maybeSingle();

  if (error) throw new StoreSettingsError(error.message);
  if (updated) return;

  /*
   * No row matched. Either it has never existed, or RLS filtered it. The
   * insert settles which: it fails closed for a caller without
   * `settings.manage`, whose write would otherwise vanish silently.
   */
  const { error: insertError } = await db
    .from('store_settings')
    .insert({ account_id: scope.accountId, ...payload })
    .select('account_id')
    .single();

  if (!insertError) return;

  if (insertError.code === '42501') {
    throw new StoreSettingsError('You do not have permission to change this store’s details.');
  }

  // The row appeared between the update and the insert.
  if (insertError.code === '23505') {
    throw new StoreSettingsError(
      'These details were just changed elsewhere. Reload and try again.',
    );
  }

  throw new StoreSettingsError(insertError.message);
}

/**
 * Rename the workspace.
 *
 * `accounts_self_update` allows the primary owner alone, so this fails closed
 * for anyone else rather than needing a check here.
 */
export async function saveTradingName(db: Db, scope: TenantScope, name: string): Promise<void> {
  const { data, error } = await db
    .from('accounts')
    .update({ name: name.trim() })
    .eq('id', scope.accountId)
    .select('id')
    .maybeSingle();

  if (error) throw new StoreSettingsError(error.message);

  if (!data) {
    throw new StoreSettingsError('Only the workspace owner can change the trading name.');
  }
}

/** '' -> null, so nullable columns stay empty rather than storing blanks. */
function nullable(value: string): string | null {
  const trimmed = value.trim();

  return trimmed === '' ? null : trimmed;
}

/** Merchants type `acme.com`; the column is a link, so give it a scheme. */
function normalizeUrl(value: string): string | null {
  const trimmed = value.trim();

  if (trimmed === '') return null;

  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
}
